import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import i18n from '../i18n';
import { useAuth } from './AuthContext';
import { updateUserPreferences } from '../api/endpointsBackend';
import { ALERT_KEYS } from '../utils/movementAlerts';

// MI-282 Rev2: per-user UI preferences (alert-display toggles + language).
// MI-297: + onboarding state (wizard seen, checklist dismissed, per-step acks)
// and country. The DB is the source of truth (hydrated on login from
// user.preferences); localStorage is a fast cache so the first paint isn't
// blank/flashing (and the wizard doesn't flicker before hydration).
const ALERTS_CACHE_KEY = 'mi-kapital-alert-prefs';
const LEGACY_STATEMENT_KEY = 'mi-kapital-statement-alerts'; // old single toggle
const ONBOARDING_CACHE_KEY = 'mi-kapital-onboarding';
const COUNTRY_CACHE_KEY = 'mi-kapital-country';
// MI-337: the user's main currency. Resolved from the country by the onboarding
// wizard and stored, so the backend (the MCP) can default to it without owning
// a country->currency map of its own.
const HOME_CURRENCY_CACHE_KEY = 'mi-kapital-home-currency';
// Optional features each user turns on for themselves (e.g. Goals).
const FEATURES_CACHE_KEY = 'mi-kapital-features';

// Read the cached alert map, migrating the old single statement toggle.
function readAlertCache() {
  try {
    const raw = localStorage.getItem(ALERTS_CACHE_KEY);
    if (raw) return JSON.parse(raw) || {};
    if (localStorage.getItem(LEGACY_STATEMENT_KEY) === 'false') {
      return { needsSourceFile: false };
    }
  } catch (e) { /* ignore */ }
  return {};
}

function writeAlertCache(map) {
  try { localStorage.setItem(ALERTS_CACHE_KEY, JSON.stringify(map)); } catch (e) { /* ignore */ }
}

function readOnboardingCache() {
  try { return JSON.parse(localStorage.getItem(ONBOARDING_CACHE_KEY)) || {}; }
  catch (e) { return {}; }
}

function writeOnboardingCache(map) {
  try { localStorage.setItem(ONBOARDING_CACHE_KEY, JSON.stringify(map)); } catch (e) { /* ignore */ }
}

function readFeaturesCache() {
  try { return JSON.parse(localStorage.getItem(FEATURES_CACHE_KEY)) || {}; }
  catch (e) { return {}; }
}

function writeFeaturesCache(map) {
  try { localStorage.setItem(FEATURES_CACHE_KEY, JSON.stringify(map)); } catch (e) { /* ignore */ }
}

const PreferencesContext = createContext(null);

export function PreferencesProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  // alerts: map of alertKey -> bool. A key absent (or not false) means "on".
  const [alerts, setAlertsState] = useState(readAlertCache);
  // onboarding: { wizardSeenAt, checklistDismissedAt, categoriesAckAt, ... }
  const [onboarding, setOnboardingState] = useState(readOnboardingCache);
  // features: map of featureKey -> bool. Unlike alerts, an absent key means off.
  const [features, setFeaturesState] = useState(readFeaturesCache);
  const [country, setCountryState] = useState(() => localStorage.getItem(COUNTRY_CACHE_KEY) || null);
  const [homeCurrency, setHomeCurrencyState] = useState(
    () => localStorage.getItem(HOME_CURRENCY_CACHE_KEY) || null,
  );
  // MI-329: which rates convert USD↔COP in the workspaces this user owns
  // ('trm' | 'market'). Null until they touch the switch: the server then
  // decides from their country, and says so as each workspace's `rate_source`.
  const [rateSource, setRateSourceState] = useState(null);
  const hydratedFor = useRef(null);

  // Hydrate from the backend once per logged-in user: DB is source of truth.
  useEffect(() => {
    const prefs = user?.preferences;
    if (!user?.id || hydratedFor.current === user.id) return;
    hydratedFor.current = user.id;
    if (prefs && typeof prefs === 'object') {
      if (prefs.alerts && typeof prefs.alerts === 'object') {
        setAlertsState(prefs.alerts);
        writeAlertCache(prefs.alerts);
      }
      // DB is source of truth: an absent `onboarding` means a clean slate, so
      // reset to {} rather than inheriting a prior user's cached flags on a
      // shared browser (which would suppress the wizard for a genuine new user).
      const ob = (prefs.onboarding && typeof prefs.onboarding === 'object')
        ? prefs.onboarding : {};
      setOnboardingState(ob);
      writeOnboardingCache(ob);
      // Same clean-slate rule: a prior user's switches must not carry over.
      const feats = (prefs.features && typeof prefs.features === 'object')
        ? prefs.features : {};
      setFeaturesState(feats);
      writeFeaturesCache(feats);
      if (prefs.country) {
        setCountryState(prefs.country);
        try { localStorage.setItem(COUNTRY_CACHE_KEY, prefs.country); } catch (e) { /* ignore */ }
      }
      if (prefs.home_currency) {
        setHomeCurrencyState(prefs.home_currency);
        try { localStorage.setItem(HOME_CURRENCY_CACHE_KEY, prefs.home_currency); } catch (e) { /* ignore */ }
      }
      setRateSourceState(prefs.rate_source || null);
      if (prefs.language && prefs.language !== i18n.resolvedLanguage) {
        i18n.changeLanguage(prefs.language);
      }
    }
  }, [user?.id, user?.preferences]);

  // Reset the hydration guard on logout so the next user re-hydrates, and drop
  // the per-user caches so the next account on this browser doesn't inherit
  // them (e.g. a new user seeing no wizard because of a prior user's flags).
  useEffect(() => {
    if (!isAuthenticated) {
      hydratedFor.current = null;
      try {
        localStorage.removeItem(ONBOARDING_CACHE_KEY);
        localStorage.removeItem(ALERTS_CACHE_KEY);
        localStorage.removeItem(COUNTRY_CACHE_KEY);
        localStorage.removeItem(HOME_CURRENCY_CACHE_KEY);
        localStorage.removeItem(FEATURES_CACHE_KEY);
      } catch (e) { /* ignore */ }
      setRateSourceState(null);
    }
  }, [isAuthenticated]);

  // Best-effort write-through to the backend (cache already updated locally).
  // The promise settles once the write has, and never rejects: a caller about
  // to leave the page can wait for it, everyone else ignores it.
  const persist = useCallback((patch) => {
    if (!isAuthenticated) return Promise.resolve();
    return updateUserPreferences(patch).catch(() => { /* offline / transient — cache holds */ });
  }, [isAuthenticated]);

  const setAlertEnabled = useCallback((key, value) => {
    setAlertsState((prev) => {
      const next = { ...prev, [key]: value };
      writeAlertCache(next);
      return next;
    });
    persist({ alerts: { [key]: value } });
  }, [persist]);

  const setAllAlerts = useCallback((value) => {
    const next = {};
    ALERT_KEYS.forEach((k) => { next[k] = value; });
    writeAlertCache(next);
    setAlertsState(next);
    persist({ alerts: next });
  }, [persist]);

  const setLanguage = useCallback((code) => {
    i18n.changeLanguage(code); // also updates the i18next localStorage cache
    persist({ language: code });
  }, [persist]);

  const setCountry = useCallback((code) => {
    setCountryState(code);
    try { localStorage.setItem(COUNTRY_CACHE_KEY, code); } catch (e) { /* ignore */ }
    persist({ country: code });
  }, [persist]);

  const setHomeCurrency = useCallback((code) => {
    if (!code) return;
    setHomeCurrencyState(code);
    try { localStorage.setItem(HOME_CURRENCY_CACHE_KEY, code); } catch (e) { /* ignore */ }
    persist({ home_currency: code });
  }, [persist]);

  // Returns the write, so the caller can wait for it before reloading.
  const setRateSource = useCallback((code) => {
    setRateSourceState(code);
    return persist({ rate_source: code });
  }, [persist]);

  // Merge a partial patch into the onboarding map (backend merges key-by-key).
  const patchOnboarding = useCallback((patch) => {
    setOnboardingState((prev) => {
      const next = { ...prev, ...patch };
      writeOnboardingCache(next);
      return next;
    });
    return persist({ onboarding: patch });
  }, [persist]);

  // The wizard first auto-opened for this (new) user. Cohort marker that makes
  // cross-session resume migration-safe — existing users never get it stamped.
  const markWizardStarted = useCallback(() => {
    if (onboarding.wizardStartedAt) return; // stamp once
    patchOnboarding({ wizardStartedAt: new Date().toISOString() });
  }, [onboarding.wizardStartedAt, patchOnboarding]);

  // Reached the celebration (last step): setup was walked to the end. Stops the
  // resume rule from reopening and gates the coach-mark tour.
  const markWizardCompleted = useCallback(() => {
    if (onboarding.wizardCompletedAt) return;
    patchOnboarding({ wizardCompletedAt: new Date().toISOString() });
  }, [onboarding.wizardCompletedAt, patchOnboarding]);

  // User closed the wizard with ✕ ("not now"): suppress auto-reopen, the
  // non-blocking checklist takes over. Onboarding blocks now, so nothing calls
  // this any more; kept because users who dismissed the old modal still carry
  // the flag and must keep falling through.
  const markWizardDismissed = useCallback(() => {
    if (onboarding.wizardDismissedAt) return;
    patchOnboarding({ wizardDismissedAt: new Date().toISOString() });
  }, [onboarding.wizardDismissedAt, patchOnboarding]);

  // The step onboarding is on, so leaving and coming back lands on the same
  // screen. Derived resume can't do this: the regional steps aren't tracked, so
  // someone who left on "country" would come back to "account".
  const setOnboardingStep = useCallback((stepKey) => {
    if (!stepKey || onboarding.currentStepKey === stepKey) return;
    patchOnboarding({ currentStepKey: stepKey });
  }, [onboarding.currentStepKey, patchOnboarding]);

  // The account step saved a real account. Not `accounts > 0`: onboarding makes
  // a cash account of its own, and that one must not stand in for this.
  //
  // The id is kept too, not just the timestamp: the closing step defaults both
  // the first movement and the statement upload to this account, and React
  // state would lose it the moment the user reloads and resumes.
  const markAccountCreated = useCallback((accountId) => {
    if (onboarding.accountCreatedAt) return;
    patchOnboarding({
      accountCreatedAt: new Date().toISOString(),
      accountId: accountId ?? null,
    });
  }, [onboarding.accountCreatedAt, patchOnboarding]);

  // The cash account onboarding creates once the country step resolves the
  // currency. Stamped so re-entering the step never makes a second one.
  const markCashAccountSeeded = useCallback(() => {
    patchOnboarding({ cashAccountAt: new Date().toISOString() });
  }, [patchOnboarding]);

  const dismissChecklist = useCallback(() => {
    patchOnboarding({ checklistDismissedAt: new Date().toISOString() });
  }, [patchOnboarding]);

  // Phase 3: coach-mark tour shown once.
  const markTourSeen = useCallback(() => {
    patchOnboarding({ tourSeenAt: new Date().toISOString() });
  }, [patchOnboarding]);

  // --- The guest flow (invited members) ---
  // The backend writes `inviteWorkspaceId`, `inviteRole` and `inviteAcceptedAt`
  // when a first-run user accepts an invitation; these three are the
  // frontend's half. Flat keys like every other flag, because both sides merge
  // `onboarding` one level deep and a nested object would be replaced whole.

  // The guest step the user is on. Its own key, not `currentStepKey`, so the
  // owner flow and the guest flow can never resume each other.
  const setInviteStep = useCallback((stepKey) => {
    if (!stepKey || onboarding.inviteStepKey === stepKey) return;
    patchOnboarding({ inviteStepKey: stepKey });
  }, [onboarding.inviteStepKey, patchOnboarding]);

  // Left the guest flow through its last button: stops holding them, and
  // opens the way for the guest tour. Returns the write, because that button
  // leaves with a full page load, which reads preferences back from the
  // server: it waits for the stamp to land first.
  const markInviteCompleted = useCallback(() => {
    if (onboarding.inviteCompletedAt) return Promise.resolve();
    return patchOnboarding({ inviteCompletedAt: new Date().toISOString() });
  }, [onboarding.inviteCompletedAt, patchOnboarding]);

  // The guest tour ran (or was closed). Separate from the owner's
  // `tourSeenAt`, so neither tour can use up the other.
  const markInviteTourSeen = useCallback(() => {
    if (onboarding.inviteTourSeenAt) return;
    patchOnboarding({ inviteTourSeenAt: new Date().toISOString() });
  }, [onboarding.inviteTourSeenAt, patchOnboarding]);

  // Acknowledge an optional step ('categories' | 'currencies').
  const ackStep = useCallback((name) => {
    patchOnboarding({ [`${name}AckAt`]: new Date().toISOString() });
  }, [patchOnboarding]);

  const setFeatureEnabled = useCallback((key, value) => {
    setFeaturesState((prev) => {
      const next = { ...prev, [key]: value };
      writeFeaturesCache(next);
      return next;
    });
    persist({ features: { [key]: value } });
  }, [persist]);

  const isAlertEnabled = useCallback((key) => alerts[key] !== false, [alerts]);
  // Off until the user turns it on — the opposite of isAlertEnabled.
  const isFeatureEnabled = useCallback((key) => features[key] === true, [features]);
  const allAlertsEnabled = ALERT_KEYS.every((k) => alerts[k] !== false);

  const value = {
    alerts,
    isAlertEnabled,
    setAlertEnabled,
    allAlertsEnabled,
    setAllAlerts,
    isFeatureEnabled,
    setFeatureEnabled,
    setLanguage,
    country,
    setCountry,
    homeCurrency,
    setHomeCurrency,
    rateSource,
    setRateSource,
    onboarding,
    patchOnboarding,
    markWizardStarted,
    markWizardCompleted,
    markWizardDismissed,
    setOnboardingStep,
    markAccountCreated,
    markCashAccountSeeded,
    dismissChecklist,
    markTourSeen,
    setInviteStep,
    markInviteCompleted,
    markInviteTourSeen,
    ackStep,
  };

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences() {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences must be used inside PreferencesProvider');
  return ctx;
}
